import { AutogenLayoutCommissionsRates } from "./autogen/AutogenLayoutCommissionsRates";
import {
    DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownSelectionEvent,
    Label, Layout, SaveButton, Snackbar, Tab, Table, TableRow, TableRowDisplayEvent, Textbox, ValidationResult
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { Api, Currency, Navigation } from "@mcleod/core";
import { CopyCommissionRate } from "./CopyCommissionRate";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";

enum RateType {
    PER_ORDER = "P",
    TIERED = "T"
}

enum TieredRateType {
    COMMISSIONABLE_MARGIN = "C",
    ORDER_COUNT = "O",
    BOTH = "B"
}

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "commission_rate",
    screenName: "Commissions Rates",
    navigateToUrl: "lme/settlement/commissions/CommissionsRates?mode=update&key="
};

export class CommissionsRates extends AutogenLayoutCommissionsRates {

    onLoad(): void | Promise<void> {
        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
        this.styleTab(this.tabComments);
        this.sourceComments.addAfterExecutionListener(() => {
            this.updateTabCountLabel(this.tabComments, this.layoutBackOfficeCommentsTable.tableComments);
        });
        this.layoutBackOfficeCommentsTable.tableComments.addContentsChangedListener(() => {
            this.updateTabCountLabel(this.tabComments, this.layoutBackOfficeCommentsTable.tableComments);
        });
        this.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
            if (event.newMode === DataSourceMode.SEARCH) {
                this.panelRates.visible = false;
            }
            if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
                this.configureRatesSection();
                this.setComponentListeners();
            }
        });
        this.mainDataSource.addAfterSearchPlusChildrenListener(() => {
            this.resetSaveButton();
        });
        this.sourceCommissionRateTier.addAfterExecutionListener(() => {
            this.setFirstColumnSort();
        });
        this.sourceCommissionRatePerOrder.addAfterExecutionListener(() => {
            this.setFirstColumnSort();
        });
        this.mainDataSource.addAfterExecutionListener(async (event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.SEARCH) {
                const response = await Api.search("lme/settlement/commissions/check-rate-for-pending-commissions", {
                    rate_code: this.activeRow.get("id")
                });
                const rateExists = response.data?.[0]?.rate_exists;
                if (rateExists) {
                    Snackbar.showSnackbar("Pending commissions exist for this Rate Code. If updates are made, Commissions must be re-processed.", { persist: true });
                }
            }
        });
    }

    resetSaveButton() {
        if (this.activeRow == null) return;
        const saveButton = this.dataheaderCommissionsRates["saveButton"] as SaveButton;
        if (saveButton) {
            saveButton.doOnDataSourceChanged(this.sourceCommissionRate, false);
        }
    }

    private setFirstColumnSort() {
        this.tableRatesPerOrder.applySort("revenue_code_id");
        this.tableRatesTieredBoth.applySort("starting_margin_amount");
        this.tableRatesTieredCommMargin.applySort("starting_margin_amount");
        this.tableRatesTieredOrderCount.applySort("starting_order_count");
    }

    private setComponentListeners() {
        this.textboxRateType.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (this.sourceCommissionRateTier.data.length > 0 || this.sourceCommissionRatePerOrder.data.length > 0) {
                CommonDialogs.showYesNo("Tiered rates will be deleted when changing the tiered rate basis. Proceed with change?", "Change Tiered Rate Basis").then(async (isYes: boolean) => {
                    if (isYes) {
                        this.clearRateTables();
                        this.configureRatesSection();
                    } else {
                        this.activeRow.set("rate_type", event.deselection?.value);
                    }
                });
            } else {
                this.configureRatesSection();
            }
        });
        this.textboxTieredRateBasis.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (this.sourceCommissionRateTier.data.length > 0 || this.sourceCommissionRatePerOrder.data.length > 0) {
                CommonDialogs.showYesNo("Tiered rates will be deleted when changing the tiered rate basis. Proceed with change?", "Change Tiered Rate Basis").then(async (isYes: boolean) => {
                    if (isYes) {
                        this.clearRateTables();
                        this.configureRatesSection();
                    } else {
                        this.activeRow.set("tiered_rate_basis", event.deselection?.value);
                    }
                });
            } else {
                this.configureRatesSection();
            }
        });
        this.tableRatesTieredOrderCount.addContentsChangedListener(() => this.calculateEndOrderCounts(this.tableRatesTieredOrderCount.rows));
        this.tableRatesTieredCommMargin.addContentsChangedListener(() => this.calculateEndCommMargins(this.tableRatesTieredCommMargin.rows));
        this.tableRatesTieredBoth.addContentsChangedListener(() => this.calculateRateTieredBoth());
    }

    tableRatesTieredOrderCountOnRowDisplay(event: TableRowDisplayEvent) {
        this.addRowValidationListeners(event.getTableRow());
        this.calculateEndOrderCounts(this.tableRatesTieredOrderCount.rows)
    }

    tableRatesTieredCommMarginOnRowDisplay(event: TableRowDisplayEvent) {
        this.addRowValidationListeners(event.getTableRow());
        this.calculateEndCommMargins(this.tableRatesTieredCommMargin.rows)
    }

    tableRatesTieredBothOnRowDisplay(event: TableRowDisplayEvent) {
        this.addRowValidationListeners(event.getTableRow());
        this.calculateRateTieredBoth();
    }

    tableRatesPerOrderOnRowDisplay(event: TableRowDisplayEvent) {
        this.addRowValidationListeners(event.getTableRow());
    }

    addRowValidationListeners(tableRow: TableRow) {
        const marginTextbox = tableRow.findComponentsByField("starting_margin_amount")?.[0] as Textbox;
        if (marginTextbox) {
            marginTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                if (mode === DataSourceMode.SEARCH) {
                    return null;
                }
                if (tableRow.table === this.tableRatesTieredCommMargin) {
                    return this.validateRateTieredCommMargin(marginTextbox, tableRow);
                } else if (tableRow.table === this.tableRatesTieredBoth) {
                    return this.validateRateTieredBoth(marginTextbox, tableRow);
                }
                return null;
            };
        }

        const orderCountTextbox = tableRow.findComponentsByField("starting_order_count")?.[0] as Textbox;
        if (orderCountTextbox) {
            orderCountTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                if (mode === DataSourceMode.SEARCH) {
                    return null;
                }
                if (tableRow.table === this.tableRatesTieredOrderCount) {
                    return this.validateOrderCount(orderCountTextbox, tableRow);
                } else if (tableRow.table === this.tableRatesTieredBoth) {
                    return this.validateRateTieredBoth(orderCountTextbox, tableRow);
                }
                return null;
            };
        }

        const revenueCodeTextbox = tableRow.findComponentsByField("revenue_code_id")?.[0] as Textbox;
        if (revenueCodeTextbox) {
            revenueCodeTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                return this.validatePerOrderRate(revenueCodeTextbox, tableRow, mode);
            };
        }

        const orderTypeTextbox = tableRow.findComponentsByField("order_type_id")?.[0] as Textbox;
        if (orderTypeTextbox) {
            orderTypeTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                return this.validatePerOrderRate(orderTypeTextbox, tableRow, mode);
            };
            const percentageTextbox = tableRow.findComponentsByField("commission_percent")?.[0] as Textbox;
            if (percentageTextbox) {
                percentageTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                    return this.validatePerOrderRate(orderTypeTextbox, tableRow, mode);
                };
            }
        }
    }

    private clearRateTables() {
        for (let i = this.tableRatesTieredOrderCount.rows.length - 1; i >= 0; i--) {
            this.tableRatesTieredOrderCount.rows[i].deleteRow();
        }
        for (let i = this.tableRatesTieredCommMargin.rows.length - 1; i >= 0; i--) {
            this.tableRatesTieredCommMargin.rows[i].deleteRow();
        }
        for (let i = this.tableRatesTieredBoth.rows.length - 1; i >= 0; i--) {
            this.tableRatesTieredBoth.rows[i].deleteRow();
        }
        for (let i = this.tableRatesPerOrder.rows.length - 1; i >= 0; i--) {
            this.tableRatesPerOrder.rows[i].deleteRow();
        }
    }

    private configureRatesSection() {
        const isTiered: boolean = this.activeRow.get("rate_type") === RateType.TIERED;
        this.textboxTieredRateBasis.enabled = isTiered;
        this.textboxTieredRateBasis.required = isTiered;
        this.tableRatesPerOrder.visible = !isTiered;
        this.tableRatesPerOrder.fillHeight = !isTiered;
        this.tableRatesTieredCommMargin.visible = isTiered && this.activeRow.get("tiered_rate_basis") === TieredRateType.COMMISSIONABLE_MARGIN;
        this.tableRatesTieredCommMargin.fillHeight = isTiered && this.activeRow.get("tiered_rate_basis") === TieredRateType.COMMISSIONABLE_MARGIN;
        this.tableRatesTieredOrderCount.visible = isTiered && this.activeRow.get("tiered_rate_basis") === TieredRateType.ORDER_COUNT;
        this.tableRatesTieredOrderCount.fillHeight = isTiered && this.activeRow.get("tiered_rate_basis") === TieredRateType.ORDER_COUNT;
        this.tableRatesTieredBoth.visible = isTiered && this.activeRow.get("tiered_rate_basis") === TieredRateType.BOTH;
        this.tableRatesTieredBoth.fillHeight = isTiered && this.activeRow.get("tiered_rate_basis") === TieredRateType.BOTH;
        this.setFirstColumnSort();
        if (!isTiered) {
            this.activeRow.set("tiered_rate_basis", null);
        }
    }

    private validateRateTieredCommMargin(textbox: Textbox, tableRow: TableRow): ValidationResult {
        const result: ValidationResult = {
            component: textbox,
            isValid: true
        }
        const table = tableRow.table as Table;
        const marginAmount = tableRow.data.getCurrency("starting_margin_amount")?.amount;
        const duplicateMargin = table.rows.find(row => row.data.getCurrency("starting_margin_amount").amount === marginAmount && row !== tableRow);
        if (duplicateMargin) {
            result.isValid = false;
            result.validationMessage = "This begin commissionable margin already exists";
        }
        return result;
    }

    private validateRateTieredBoth(textbox: Textbox, tableRow: TableRow): ValidationResult {
        const result: ValidationResult = {
            component: textbox,
            isValid: true
        }
        const table = tableRow.table as Table;
        const marginAmount = tableRow.data.getCurrency("starting_margin_amount")?.amount;
        const orderCount = tableRow.data.get("starting_order_count");
        const duplicateRate = table.rows.find(row => row.data.getCurrency("starting_margin_amount")?.amount === marginAmount && row.data.get("starting_order_count") === orderCount && row !== tableRow);
        if (duplicateRate) {
            result.isValid = false;
            result.validationMessage = "This begin order count already exists with this starting margin amount";
        }
        return result;
    }

    private validateOrderCount(textbox: Textbox, tableRow: TableRow): ValidationResult {
        const result: ValidationResult = {
            component: textbox,
            isValid: true
        }
        const table = tableRow.table as Table;
        const orderCount = tableRow.data.get("starting_order_count");
        const duplicateOrderCount = table.rows.find(row => row.data.get("starting_order_count") === orderCount && row !== tableRow);
        if (duplicateOrderCount) {
            result.isValid = false;
            result.validationMessage = "This begin order count already exists";
        }
        return result;
    }

    private calculateEndOrderCounts(rows: TableRow[]) {
        rows.forEach((row: TableRow) => {
            const startingValue = row.data.get("starting_order_count");
            const nextRow = rows.filter(r => r.data.get("starting_order_count") > startingValue).sort((a, b) => a.data.get("starting_order_count") - b.data.get("starting_order_count"))[0];
            if (nextRow) {
                if (nextRow.data.get("starting_order_count") > startingValue) {
                    const labelEndOrderCount = row.findComponentById("labelEndOrderCount") as Label ?? row.findComponentById("labelEndOrderCountBoth") as Label;
                    if (labelEndOrderCount) {
                        row.data.set("end_order_count", nextRow.data.get("starting_order_count") - 1 + "");
                    }
                }
            }
        });
    }

    private calculateEndCommMargins(rows: TableRow[]) {
        rows.forEach((row: TableRow) => {
            const startingValue = row.data.getCurrency("starting_margin_amount")?.amount;
            const nextRow = rows.filter(r => r.data.getCurrency("starting_margin_amount")?.amount > startingValue).sort((a, b) => a.data.getCurrency("starting_margin_amount")?.amount - b.data.getCurrency("starting_margin_amount")?.amount)[0];
            if (nextRow) {
                if (nextRow.data.getCurrency("starting_margin_amount")?.amount > startingValue) {
                    const labelEndCommMargin = row.findComponentById("labelEndCommMargin") as Label ?? row.findComponentById("labelEndCommMarginBoth") as Label;
                    if (labelEndCommMargin) {
                        row.data.set("end_margin_amount", new Currency({ amount: nextRow.data.getCurrency("starting_margin_amount")?.amount - 0.01, currency_code: row.data.getCurrency("starting_margin_amount")?.currency_code}))
                    }
                }
            }
        });
    }

    private calculateRateTieredBoth() {
        this.calculateEndCommMargins(this.tableRatesTieredBoth.rows);
        this.tableRatesTieredBoth.rows.forEach((row: TableRow) => {
            const startingValue = row.data.getCurrency("starting_margin_amount")?.amount;
            // find all rows with the same starting margin amount
            const marginAmountRows = this.tableRatesTieredBoth.rows.filter(r => r.data.getCurrency("starting_margin_amount")?.amount === startingValue);
            // set end order count for all rows with the same starting margin amount
            this.calculateEndOrderCounts(marginAmountRows);
        });
    }

    private validatePerOrderRate(textbox: Textbox, tableRow: TableRow, mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const result: ValidationResult = {
            component: textbox,
            isValid: true
        }
        const table = tableRow.table as Table;
        const revenueCodeId = tableRow.data.get("revenue_code_id");
        const orderTypeId = tableRow.data.get("order_type_id");
        const duplicateRate = table.rows.find(row => row.data.get("revenue_code_id") == revenueCodeId && row.data.get("order_type_id") == orderTypeId && row !== tableRow);
        if (duplicateRate) {
            result.isValid = false;
            result.validationMessage = "Row already exists for this revenue code and order type";
        }
        return result;
    }

    private styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.setProps({ color: "subtle.light", borderWidth: 0 });
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    updateTabCountLabel(tab: Tab, table: Table) {
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.dataSource.rowCount + "";
        }
    }

    private async copyRate() {
        const layout = Layout.getLayout("lme/settlement/commissions/CopyCommissionRate") as CopyCommissionRate;
        return CommonDialogs.showYesNo(layout, "Copy Code", {
            yesButtonProps: {
                caption: "Copy",
                onClick: async () => {
                    if (layout.validateSimple()) {
                        await Api.post("lme/settlement/commissions/copy-commission-rate", {
                            old_rate_code: this.activeRow.get("id"),
                            new_rate_code: layout.textboxRateCode.text,
                            new_rate_description: layout.textboxDescription.text
                        });
                        await Navigation.navigateTo(`lme/settlement/commissions/CommissionsRates?mode=update&key=${layout.textboxRateCode.text}`, { newTab: true });
                    }
                }
            },
            noButtonCaption: "Cancel"
        }, false, 100);
    }
}
